import React, { useState, useEffect } from 'react';
import { X, Check, Download, FileSpreadsheet, FileText, Database } from 'lucide-react';

const AVAILABLE_COLUMNS = [
  { id: 'id', label: 'Scenario ID', category: 'basic', default: true },
  { id: 'name', label: 'Scenario Name', category: 'basic', default: true },
  { id: 'service_name', label: 'Service', category: 'basic', default: true },
  { id: 'creator_name', label: 'Creator', category: 'basic', default: true },
  { id: 'status', label: 'Status', category: 'basic', default: true },
  { id: 'created_at', label: 'Created Date', category: 'basic', default: true },
  { id: 'municipality', label: 'Municipality', category: 'location', default: false },
  { id: 'state', label: 'State', category: 'location', default: false },
  { id: 'total_gross_revenue', label: 'Gross Revenue', category: 'financial', default: true },
  { id: 'total_net_revenue', label: 'Net Revenue', category: 'financial', default: true },
  { id: 'total_payments', label: 'Total Payments', category: 'financial', default: false },
  { id: 'take_rate', label: 'Take Rate %', category: 'financial', default: false },
  { id: 'gpm', label: 'Gross Profit Margin %', category: 'financial', default: false },
  { id: 'contribution_margin', label: 'Contribution Margin', category: 'financial', default: false },
  { id: 'net_fees_margin', label: 'Net Fees Margin %', category: 'financial', default: false },
  { id: 'approval_rate', label: 'Approval Rate %', category: 'metrics', default: false },
  { id: 'submitted_at', label: 'Submitted Date', category: 'metrics', default: false },
  { id: 'approved_at', label: 'Approved Date', category: 'metrics', default: false },
];

export default function ExportCustomizationModal({ isOpen, onClose, onExport }) {
  const [selectedColumns, setSelectedColumns] = useState(() => {
    const saved = localStorage.getItem('export_columns_preference');
    if (saved) {
      return JSON.parse(saved);
    }
    return AVAILABLE_COLUMNS.filter(col => col.default).map(col => col.id);
  });
  
  const [exportFormat, setExportFormat] = useState('excel');

  useEffect(() => {
    if (isOpen) {
      // Load saved preferences when modal opens
      const saved = localStorage.getItem('export_columns_preference');
      if (saved) {
        setSelectedColumns(JSON.parse(saved));
      }
    }
  }, [isOpen]);

  const toggleColumn = (columnId) => {
    setSelectedColumns(prev => {
      if (prev.includes(columnId)) {
        return prev.filter(id => id !== columnId);
      } else {
        return [...prev, columnId];
      }
    });
  };

  const selectAll = () => {
    setSelectedColumns(AVAILABLE_COLUMNS.map(col => col.id));
  };

  const selectDefaults = () => {
    setSelectedColumns(AVAILABLE_COLUMNS.filter(col => col.default).map(col => col.id));
  };

  const clearAll = () => {
    setSelectedColumns([]);
  };

  const handleExport = () => {
    // Save preferences
    localStorage.setItem('export_columns_preference', JSON.stringify(selectedColumns));
    
    // Call export with selected columns and format
    onExport(exportFormat, selectedColumns);
    onClose();
  };

  if (!isOpen) return null;

  const categories = {
    basic: AVAILABLE_COLUMNS.filter(col => col.category === 'basic'),
    location: AVAILABLE_COLUMNS.filter(col => col.category === 'location'),
    financial: AVAILABLE_COLUMNS.filter(col => col.category === 'financial'),
    metrics: AVAILABLE_COLUMNS.filter(col => col.category === 'metrics'),
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-slate-200 p-6 flex items-center justify-between">
          <div>
            <h3 className="text-xl font-semibold text-slate-900">
              <Download className="w-5 h-5 inline mr-2 text-indigo-600" />
              Customize Export
            </h3>
            <p className="text-sm text-slate-600 mt-1">Select columns and format for your export</p>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6">
          {/* Export Format Selection */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-slate-900 mb-3">Export Format</label>
            <div className="grid grid-cols-4 gap-3">
              {[
                { value: 'excel', label: 'Excel', icon: FileSpreadsheet, color: 'green' },
                { value: 'pdf', label: 'PDF', icon: FileText, color: 'red' },
                { value: 'csv', label: 'CSV', icon: Database, color: 'blue' },
                { value: 'json', label: 'JSON', icon: Database, color: 'purple' },
              ].map(format => (
                <button
                  key={format.value}
                  onClick={() => setExportFormat(format.value)}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    exportFormat === format.value
                      ? `border-${format.color}-500 bg-${format.color}-50`
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <format.icon className={`w-6 h-6 mx-auto mb-2 ${
                    exportFormat === format.value ? `text-${format.color}-600` : 'text-slate-400'
                  }`} />
                  <p className={`text-sm font-medium ${
                    exportFormat === format.value ? `text-${format.color}-700` : 'text-slate-600'
                  }`}>
                    {format.label}
                  </p>
                </button>
              ))}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="mb-4 flex items-center justify-between">
            <label className="text-sm font-semibold text-slate-900">
              Select Columns ({selectedColumns.length} selected)
            </label>
            <div className="flex gap-2">
              <button
                onClick={selectAll}
                className="text-xs px-3 py-1 bg-indigo-100 text-indigo-700 rounded hover:bg-indigo-200 transition-colors"
              >
                Select All
              </button>
              <button
                onClick={selectDefaults}
                className="text-xs px-3 py-1 bg-slate-100 text-slate-700 rounded hover:bg-slate-200 transition-colors"
              >
                Defaults
              </button>
              <button
                onClick={clearAll}
                className="text-xs px-3 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors"
              >
                Clear All
              </button>
            </div>
          </div>

          {/* Column Selection by Category */}
          <div className="space-y-6">
            {Object.entries(categories).map(([categoryName, columns]) => (
              <div key={categoryName}>
                <h4 className="text-sm font-semibold text-slate-700 mb-3 capitalize">
                  {categoryName} Information
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {columns.map(column => (
                    <label
                      key={column.id}
                      className={`flex items-center gap-2 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                        selectedColumns.includes(column.id)
                          ? 'border-indigo-500 bg-indigo-50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedColumns.includes(column.id)}
                        onChange={() => toggleColumn(column.id)}
                        className="sr-only"
                      />
                      <div className={`w-5 h-5 rounded flex items-center justify-center border-2 ${
                        selectedColumns.includes(column.id)
                          ? 'bg-indigo-600 border-indigo-600'
                          : 'bg-white border-slate-300'
                      }`}>
                        {selectedColumns.includes(column.id) && (
                          <Check className="w-3 h-3 text-white" />
                        )}
                      </div>
                      <span className={`text-sm font-medium ${
                        selectedColumns.includes(column.id) ? 'text-indigo-900' : 'text-slate-700'
                      }`}>
                        {column.label}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-slate-50 border-t border-slate-200 p-6 flex items-center justify-between">
          <p className="text-sm text-slate-600">
            Your preferences will be saved for future exports
          </p>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-white border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleExport}
              disabled={selectedColumns.length === 0}
              className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              Export {exportFormat.toUpperCase()}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
